/**
 * Custom hook for managing user connections
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../../core/contexts/AuthContext';
import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  serverTimestamp, 
  arrayUnion, 
  arrayRemove, 
  orderBy,
  limit 
} from 'firebase/firestore';
import { db } from '../../../services/firebase/firebase';

/**
 * Custom hook for managing user connections
 * 
 * @returns {Object} Connection management methods and state
 */
const useConnections = () => {
  const { currentUser } = useAuth();
  const [connections, setConnections] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's connections
  const fetchConnections = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      const connectionsQuery = query(
        collection(db, 'connections'),
        where('users', 'array-contains', currentUser.uid)
      );
      
      const connectionsSnapshot = await getDocs(connectionsQuery);
      const userConnections = [];
      
      for (const connectionDoc of connectionsSnapshot.docs) {
        const connectionData = connectionDoc.data();
        const otherUserId = connectionData.users.find(id => id !== currentUser.uid);
        
        // Get profile data
        const profileRef = doc(db, 'profiles', otherUserId);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          userConnections.push({
            id: connectionDoc.id,
            userId: otherUserId,
            profile: {
              id: profileSnap.id,
              ...profileSnap.data()
            },
            createdAt: connectionData.createdAt
          });
        }
      }
      
      setConnections(userConnections);
    } catch (err) {
      console.error('Error fetching connections:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch pending connection requests
  const fetchPendingRequests = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      
      const requestsQuery = query(
        collection(db, 'connectionRequests'),
        where('recipientId', '==', currentUser.uid),
        where('status', '==', 'pending')
      );
      
const requestsSnapshot = await getDocs(requestsQuery);
      const requests = [];
      
      for (const requestDoc of requestsSnapshot.docs) {
        const requestData = requestDoc.data();
        
        // Get sender profile
        const profileRef = doc(db, 'profiles', requestData.senderId);
        const profileSnap = await getDoc(profileRef);
        
        if (profileSnap.exists()) {
          requests.push({
            id: requestDoc.id,
            senderId: requestData.senderId,
            message: requestData.message || '',
            profile: {
              id: profileSnap.id,
              ...profileSnap.data()
            },
            createdAt: requestData.createdAt
          });
        }
      }
      
      setPendingRequests(requests);
    } catch (err) {
      console.error('Error fetching pending requests:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check connection status with another user
  const checkConnectionStatus = async (userId) => {
    if (!currentUser) return { status: 'none' };
    
    try {
      // Check connection requests
      const sentRequestsQuery = query(
        collection(db, 'connectionRequests'),
        where('senderId', '==', currentUser.uid),
        where('recipientId', '==', userId),
        limit(1)
      );
      
      const receivedRequestsQuery = query(
        collection(db, 'connectionRequests'),
        where('senderId', '==', userId),
        where('recipientId', '==', currentUser.uid),
        limit(1)
      );
      
      const [sentSnapshot, receivedSnapshot] = await Promise.all([
        getDocs(sentRequestsQuery),
        getDocs(receivedRequestsQuery)
      ]);
      
      // Check if users are already connected
      const connectionsQuery = query(
        collection(db, 'connections'),
        where('users', 'array-contains', currentUser.uid),
        limit(10)
      );
      
      const connectionsSnapshot = await getDocs(connectionsQuery);
      const connections = connectionsSnapshot.docs.map(doc => doc.data());
      const isConnected = connections.some(conn => conn.users.includes(userId));
      
      if (isConnected) {
        return { status: 'connected' };
      }
      
      // Check request status
      if (!sentSnapshot.empty) {
        const requestDoc = sentSnapshot.docs[0];
        return { 
          status: requestDoc.data().status,
          requestId: requestDoc.id,
          direction: 'sent'
        };
      }
      
      if (!receivedSnapshot.empty) {
        const requestDoc = receivedSnapshot.docs[0];
        return { 
          status: requestDoc.data().status,
          requestId: requestDoc.id,
          direction: 'received'
        };
      }
      
      return { status: 'none' };
    } catch (err) {
      console.error('Error checking connection status:', err);
      return { status: 'error', message: err.message };
    }
  };

  // Send a connection request
  const sendConnectionRequest = async (recipientId, message = '') => {
    if (!currentUser) throw new Error('You must be logged in to send a connection request');
    
    try {
      setLoading(true);
      
      // Check if request already exists
      const existingStatus = await checkConnectionStatus(recipientId);
      
      if (existingStatus.status !== 'none') {
        throw new Error(`Connection request ${existingStatus.status}`);
      }
      
      // Create new request
      const requestData = {
        senderId: currentUser.uid,
        recipientId,
        message,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      const requestRef = await addDoc(collection(db, 'connectionRequests'), requestData);
      
      // Create notification for recipient (optional)
      await createConnectionRequestNotification(currentUser.uid, recipientId, requestRef.id);
      
      return requestRef.id;
    } catch (err) {
      console.error('Error sending connection request:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Create notification for connection request
  const createConnectionRequestNotification = async (senderId, recipientId, requestId) => {
    try {
      // Get sender profile for notification data
      const senderProfileRef = doc(db, 'profiles', senderId);
      const senderProfileSnap = await getDoc(senderProfileRef);
      
      if (senderProfileSnap.exists()) {
        const senderProfile = senderProfileSnap.data();
        
        // Create notification
        const notificationData = {
          userId: recipientId,
          type: 'connection_request',
          content: {
            requestId,
            senderId,
            senderName: senderProfile.basicInfo?.name || 'Someone',
            senderPhoto: senderProfile.photos && senderProfile.photos.length > 0 
              ? senderProfile.photos[0] 
              : null
          },
          isRead: false,
          createdAt: serverTimestamp()
        };
        
        await addDoc(collection(db, 'notifications'), notificationData);
      }
    } catch (error) {
      console.error("Error creating notification:", error);
      // Don't throw error here to prevent the main function from failing
    }
  };

  // Accept a connection request
  const acceptRequest = async (requestId) => {
    try {
      setLoading(true);
      
      const requestRef = doc(db, 'connectionRequests', requestId);
      const requestSnap = await getDoc(requestRef);
      
      if (!requestSnap.exists()) {
        throw new Error('Connection request not found');
      }
      
      const requestData = requestSnap.data();
      
      // Update request status
      await updateDoc(requestRef, {
        status: 'accepted',
        updatedAt: serverTimestamp()
      });
      
      // Create connection
      const connectionData = {
        users: [requestData.senderId, requestData.recipientId],
        createdAt: serverTimestamp(),
        requestId: requestId
      };
      
      const connectionRef = await addDoc(collection(db, 'connections'), connectionData);
      
      // Create notification for sender
      await createConnectionAcceptedNotification(requestData.recipientId, requestData.senderId);
      
      // Update local state
      await fetchPendingRequests();
      await fetchConnections();
      
      return connectionRef.id;
    } catch (err) {
      console.error('Error accepting request:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };
  
  // Create notification for accepted connection
  const createConnectionAcceptedNotification = async (acceptorId, requesterId) => {
    try {
      // Get acceptor profile
      const acceptorProfileRef = doc(db, 'profiles', acceptorId);
      const acceptorProfileSnap = await getDoc(acceptorProfileRef);
      
      if (acceptorProfileSnap.exists()) {
        const acceptorProfile = acceptorProfileSnap.data();
        
        // Create notification
        const notificationData = {
          userId: requesterId,
          type: 'connection_accepted',
          content: {
            acceptorId,
            acceptorName: acceptorProfile.basicInfo?.name || 'Someone',
            acceptorPhoto: acceptorProfile.photos && acceptorProfile.photos.length > 0 
              ? acceptorProfile.photos[0] 
              : null
          },
          isRead: false,
          createdAt: serverTimestamp()
        };
        
        await addDoc(collection(db, 'notifications'), notificationData);
      }
    } catch (error) {
      console.error("Error creating notification:", error);
    }
  };

  // Decline a connection request
  const declineRequest = async (requestId) => {
    try {
      setLoading(true);
      
      const requestRef = doc(db, 'connectionRequests', requestId);
      
      // Update status
      await updateDoc(requestRef, {
        status: 'declined',
        updatedAt: serverTimestamp()
      });
      
      // Update pending requests
      await fetchPendingRequests();
      
      return true;
    } catch (err) {
      console.error('Error declining request:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Remove a connection
  const removeConnection = async (userId) => {
    if (!currentUser) return false;
    
    try {
      setLoading(true);
      
      const connectionsQuery = query(
        collection(db, 'connections'),
        where('users', 'array-contains', currentUser.uid)
      );
      
      const connectionsSnapshot = await getDocs(connectionsQuery);
      const connectionDoc = connectionsSnapshot.docs.find(doc => {
        const data = doc.data();
        return data.users.includes(userId);
      });
      
      if (connectionDoc) {
        await deleteDoc(doc(db, 'connections', connectionDoc.id));
        
        // Update connections list
        await fetchConnections();
        
        return true;
      } else {
        throw new Error('Connection not found');
      }
    } catch (err) {
      console.error('Error removing connection:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Load initial data when user changes
  useEffect(() => {
    if (currentUser) {
      fetchConnections();
      fetchPendingRequests();
    }
  }, [currentUser]);

  return {
    connections,
    pendingRequests,
    loading,
    error,
    checkConnectionStatus,
    sendConnectionRequest,
    acceptRequest,
    declineRequest,
    removeConnection,
    refreshConnections: fetchConnections,
    refreshPendingRequests: fetchPendingRequests
  };
};

export default useConnections;